import { forwardRef } from 'react';
import { graphql } from 'gatsby';
import clsx from 'clsx';
import { ArrowUpRight } from '@insify/design-system/icons';
import { Section } from '~components/Section';
import { Image } from '~components/Image';
import { Heading } from '~components/Heading';
import { Button } from '~components/Button';
import { ComponentLink } from '~components/ComponentLink';
import { BottomBar } from './BottomBar/BottomBar';
import { HeroOverlay } from './HeroOverlay/HeroOverlay';
import { HeroProvider, useHero } from './useHero';

import * as styles from './ComponentProductTiles.module.css';

import type { ContentBlock } from '~components/types';
import type { ContentfulComponentProductTiles, ContentfulComponentProductTilesItem } from './types';

type ComponentProductTilesProps = {
  content: ContentfulComponentProductTiles;
};

export const ComponentProductTiles: ContentBlock<ComponentProductTilesProps> = (props) => (
  <HeroProvider content={props.content}>
    <ComponentProductTilesInner {...props} />
  </HeroProvider>
);

const ComponentProductTilesInner: ContentBlock<ComponentProductTilesProps> = ({ content, ...sectionProps }) => {
  const { animationState, registerHeroCard } = useHero();
  const hasVideo = !!content.video && content.video.length > 0;
  const items = content.items.slice(0, 3);

  return (
    <Section
      {...sectionProps}
      className={clsx(styles.section, styles.boxed, hasVideo && styles.hero + ' ' + styles[animationState])}
      blockLayout='boxed'
    >
      <div className={styles.cards} style={{ '--gridTemplateColumns': items.length } as React.CSSProperties}>
        {items.map((item, index) => (
          <BaseCard
            key={index}
            item={item}
            className={clsx(
              animationState === 'initial' && (index === 0 || index === content.items.length - 1) && styles.shifted,
            )}
          />
        ))}
        {hasVideo && animationState !== 'finished' && <HeroCard {...registerHeroCard()} content={content} />}
      </div>
      {hasVideo && <BottomBar />}
    </Section>
  );
};

type BaseCardProps = JSX.IntrinsicElements['article'] & {
  item: ContentfulComponentProductTilesItem;
};

const BaseCard: React.FC<BaseCardProps> = ({ item, ...props }) => (
  <article {...props} className={clsx(styles.cardOuter, styles.baseCard, props.className)}>
    <ComponentLink className={styles.card} link={item.link}>
      <Image image={item.image} className={styles.image} loading='eager' />
      <CardContent item={item} />
    </ComponentLink>
    {item.readMoreLink && (
      <ComponentLink className={styles.readMore} link={item.readMoreLink}>
        {item.readMoreLink.label}
        <ArrowUpRight className={styles.arrow} aria-hidden focusable={false} />
      </ComponentLink>
    )}
  </article>
);

type HeroCardProps = Omit<JSX.IntrinsicElements['div'], 'content'> & ComponentProductTilesProps;

const HeroCard = forwardRef<HTMLDivElement, HeroCardProps>(({ content, className, ...props }, ref) => {
  const item1 = content.items[0];
  const item2 = content.items[1];

  return (
    <div {...props} className={clsx(styles.cardOuter, styles.heroCard, className)}>
      <div className={clsx(styles.card)} ref={ref}>
        <div className={styles.cardInner}>
          <Image className={styles.image} image={item1.image} loading='eager' />
          {item2 && (
            <Image image={item2.image} className={clsx(styles.image, styles.heroCardContent)} loading='eager' />
          )}
          <HeroOverlay
            className={styles.heroOverlay}
            content={{ video: content.video!, content: content.heroContent }}
          />
        </div>
        <div className={styles.cardContentWrapper}>
          <CardContent item={item1} />
          {item2 && <CardContent item={item2} />}
        </div>
      </div>
    </div>
  );
});

type CardContentProps = {
  item: ContentfulComponentProductTilesItem;
};

const CardContent: React.FC<CardContentProps> = ({ item }) => (
  <div className={styles.cardContent}>
    {item.topBadge && <div className={styles.topBadge}>{item.topBadge}</div>}
    <Heading className={styles.heading} level={2}>
      {item.heading}
    </Heading>
    {item.description && <p className={styles.description}>{item.description}</p>}
    <Button className={styles.button}>{item.link.label}</Button>
  </div>
);

export const query1 = graphql`
  fragment ComponentProductTilesItem on ContentfulComponentProductTilesItem {
    __typename
    image {
      ...MixedMedia
    }
    heading
    description
    readMoreLink {
      ...ComponentLink
    }
    topBadge
    link {
      ...ComponentLink
    }
  }
`;

export const query2 = graphql`
  fragment ComponentProductTiles on ContentfulComponentProductTiles {
    __typename
    heading
    video {
      ...MixedMedia
    }
    heroContent {
      raw
      references {
        ...ComponentLink
      }
    }
    items {
      ...ComponentProductTilesItem
    }
  }
`;
